import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Phone, MapPin, Clock, MessageSquare, Shield, CheckCircle, AlertCircle, Navigation } from 'lucide-react';

import EmergencyBailAssistanceForm from '../landing-page/components/EmergencyBailAssistanceForm';


const ContactUs = () => {
  const [activeTab, setActiveTab] = useState('form');
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [primaryPhone] = useState('(916) 256-3468');
  const [emergencyPhone] = useState('(916) 226-3934');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Office information
  const officeInfo = {
    address: '901 H Street, Suite 103',
    city: 'Sacramento',
    state: 'CA',
    zip: '95814',
    fullAddress: '901 H Street, Suite 103, Sacramento, CA 95814',
    phone: primaryPhone,
    emergencyPhone: emergencyPhone,
    email: 'damian@sailorsbail.com',
    hours: {
      weekdays: '24/7 - Open All Day, Every Day',
      weekends: '24/7 - Open All Day, Every Day',
      holidays: '24/7 - Open All Day, Every Day'
    }
  };

  const handleCopyAddress = () => {
    navigator.clipboard?.writeText(officeInfo?.fullAddress);
    setCopiedAddress(true);
    setTimeout(() => setCopiedAddress(false), 2000);
  };

  const handleGetDirections = () => {
    const encodedAddress = encodeURIComponent(officeInfo?.fullAddress);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - 24/7 Emergency Bail Help | Sailors Bail Bonds Sacramento</title>
        <meta 
          name="description" 
          content="Reach Sailors Bail Bonds 24/7 for emergency bail assistance in Sacramento. Call (916) 256-3468 or visit us at 901 H Street, Suite 103. Licensed agents available now." 
        />
        <meta 
          name="keywords" 
          content="contact bail bonds sacramento, 24/7 bail help, emergency bail assistance, sacramento bail bonds office" 
        />
        
        {/* Structured Data for Local Business */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sailors Bail Bonds",
            "description": "24/7 Emergency Bail Bond Services in Sacramento, CA",
            "telephone": primaryPhone,
            "email": officeInfo?.email,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": officeInfo?.address,
              "addressLocality": officeInfo?.city,
              "addressRegion": officeInfo?.state,
              "postalCode": officeInfo?.zip,
              "addressCountry": "US"
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              "opens": "00:00",
              "closes": "23:59"
            },
            "priceRange": "$$",
            "areaServed": {
              "@type": "City",
              "name": "Sacramento"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center bg-red-500 px-6 py-3 rounded-full mb-6 animate-pulse">
                <span className="w-3 h-3 bg-white rounded-full mr-3"></span>
                <span className="font-bold text-lg">🚨 AVAILABLE 24/7 FOR EMERGENCIES</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-black mb-4">
                Contact Us - We're Here When You Need Us Most
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Call anytime - we're listening 24/7 and ready to provide compassionate, professional bail bond assistance
              </p>
            </div>

            {/* Quick Contact Buttons */}
            <div className="grid md:grid-cols-2 gap-4 mt-12 max-w-4xl mx-auto">
              <a
                href={`tel:${primaryPhone?.replace(/\D/g, '')}`}
                className="bg-white text-blue-700 px-8 py-6 rounded-2xl font-bold text-lg flex items-center justify-center hover:bg-blue-50 transition-all transform hover:scale-105 shadow-xl"
                aria-label={`Call our main office at ${primaryPhone}`}
              >
                <Phone className="w-6 h-6 mr-3" />
                Main Office: {primaryPhone}
              </a>
              
              <a
                href={`tel:${emergencyPhone?.replace(/\D/g, '')}`}
                className="bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-6 rounded-2xl font-black text-lg flex items-center justify-center hover:from-red-700 hover:to-red-800 transition-all transform hover:scale-105 shadow-xl"
                aria-label={`Call our emergency line at ${emergencyPhone}`}
              >
                <AlertCircle className="w-6 h-6 mr-3" />
                🚨 Emergency: {emergencyPhone}
              </a>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              {/* Office Location Card */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-blue-100">
                <div className="flex items-center mb-4">
                  <MapPin className="w-6 h-6 text-blue-600 mr-3" />
                  <h2 className="text-xl font-bold text-gray-900">Our Office</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-700 font-semibold">{officeInfo?.address}</p>
                    <p className="text-gray-700">{officeInfo?.city}, {officeInfo?.state} {officeInfo?.zip}</p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <button
                      onClick={handleCopyAddress}
                      className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors text-sm font-semibold flex items-center justify-center"
                      aria-label="Copy office address to clipboard"
                    >
                      {copiedAddress ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Address Copied!
                        </>
                      ) : (
                        <>
                          <MapPin className="w-4 h-4 mr-2" />
                          Copy Address
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleGetDirections}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold flex items-center justify-center"
                      aria-label="Get directions to our office on Google Maps"
                    >
                      <Navigation className="w-4 h-4 mr-2" />
                      Get Directions
                    </button>
                  </div>

                  {/* Office Features */}
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-3">Office Features:</h3>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                        Professional office building
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                        Handicap accessible
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                        Ample free parking
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                        Clean, private environment
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                        Kind, understanding staff
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Hours Card */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-green-100">
                <div className="flex items-center mb-4">
                  <Clock className="w-6 h-6 text-green-600 mr-3" />
                  <h2 className="text-xl font-bold text-gray-900">Hours</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                    <p className="font-black text-green-800 text-lg text-center">
                      🚨 OPEN 24/7 🚨
                    </p>
                    <p className="text-green-700 text-center mt-2">
                      Every day, every night - we're always here for emergency bail assistance
                    </p>
                  </div>

                  <div className="text-sm text-gray-600 space-y-2">
                    <p><strong>Weekdays:</strong> {officeInfo?.hours?.weekdays}</p>
                    <p><strong>Weekends:</strong> {officeInfo?.hours?.weekends}</p>
                    <p><strong>Holidays:</strong> {officeInfo?.hours?.holidays}</p>
                  </div>
                </div>
              </div>

              {/* Contact Methods Card */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-purple-100">
                <div className="flex items-center mb-4">
                  <MessageSquare className="w-6 h-6 text-purple-600 mr-3" />
                  <h2 className="text-xl font-bold text-gray-900">Contact Methods</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Primary Phone</p>
                    <a 
                      href={`tel:${primaryPhone?.replace(/\D/g, '')}`}
                      className="text-blue-600 hover:text-blue-800 font-bold text-lg"
                      aria-label={`Call our primary phone number ${primaryPhone}`}
                    >
                      {primaryPhone}
                    </a>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Emergency Line</p>
                    <a 
                      href={`tel:${emergencyPhone?.replace(/\D/g, '')}`}
                      className="text-red-600 hover:text-red-800 font-black text-lg"
                      aria-label={`Call our emergency line ${emergencyPhone}`}
                    >
                      {emergencyPhone}
                    </a>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Email</p>
                    <a 
                      href={`mailto:${officeInfo?.email}`}
                      className="text-blue-600 hover:text-blue-800"
                      aria-label={`Send email to ${officeInfo?.email}`}
                    >
                      {officeInfo?.email}
                    </a>
                    <p className="text-xs text-gray-500 mt-1">
                      Note: For immediate assistance, please call
                    </p>
                  </div>
                </div>
              </div>

              {/* Response Time Badge */}
              <div className="bg-gradient-to-r from-yellow-400 to-amber-500 rounded-2xl p-6 text-center shadow-xl">
                <Shield className="w-12 h-12 mx-auto mb-3 text-amber-900" />
                <p className="font-black text-2xl text-amber-900 mb-2">2-Minute Response</p>
                <p className="text-amber-800 font-semibold">
                  Licensed agent will contact you within 2 minutes of your request
                </p>
              </div>
            </div>

            {/* Right Column - Forms and Map */}
            <div className="lg:col-span-2 space-y-6">
              {/* Tab Navigation */}
              <div className="bg-white rounded-2xl shadow-xl p-2 flex gap-2">
                <button
                  onClick={() => setActiveTab('form')}
                  className={`flex-1 px-6 py-3 rounded-xl font-bold transition-all ${
                    activeTab === 'form'
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  aria-label="Switch to emergency contact form"
                  aria-pressed={activeTab === 'form'}
                >
                  Emergency Form
                </button>
                <button
                  onClick={() => setActiveTab('map')}
                  className={`flex-1 px-6 py-3 rounded-xl font-bold transition-all ${
                    activeTab === 'map' ?'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  aria-label="Switch to office location map"
                  aria-pressed={activeTab === 'map'}
                >
                  Office Location
                </button>
              </div>

              {/* Emergency Form Tab */}
              {activeTab === 'form' && (
                <div 
                  role="tabpanel" 
                  aria-labelledby="form-tab"
                  className="animate-fade-in"
                >
                  <EmergencyBailAssistanceForm />
                </div>
              )}

              {/* Map Tab */}
              {activeTab === 'map' && (
                <div 
                  role="tabpanel" 
                  aria-labelledby="map-tab"
                  className="bg-white rounded-2xl shadow-xl p-6 animate-fade-in"
                >
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Visit Our Sacramento Office
                    </h2>
                    <p className="text-gray-700">
                      Located in downtown Sacramento with easy access and plenty of parking. 
                      Our office is professional, private, and designed to make you feel comfortable during difficult times.
                    </p>
                  </div>

                  {/* Google Maps Embed */}
                  <div className="rounded-xl overflow-hidden border-2 border-gray-200 shadow-lg">
                    <iframe
                      title="Sailors Bail Bonds Office Location - 901 H Street, Suite 103, Sacramento, CA 95814"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3120.1234567890!2d-121.4944199!3d38.5816175!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809ad0e3e3e3e3e3%3A0x1234567890abcdef!2s901%20H%20St%2C%20Sacramento%2C%20CA%2095814!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                      width="100%"
                      height="450"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="w-full"
                      aria-label="Interactive map showing Sailors Bail Bonds office location in Sacramento"
                    ></iframe>
                  </div>

                  {/* Directions Info */}
                  <div className="mt-6 grid md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h3 className="font-bold text-blue-900 mb-2">Parking Information</h3>
                      <p className="text-blue-800 text-sm">
                        Ample free parking available on-site. Handicap accessible parking spaces near entrance.
                      </p>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h3 className="font-bold text-green-900 mb-2">Public Transportation</h3>
                      <p className="text-green-800 text-sm">
                        Easily accessible via Sacramento Regional Transit. Multiple bus routes stop within walking distance.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Trust Signals Section */}
          <div className="mt-12 bg-white rounded-2xl shadow-xl p-8 border-2 border-blue-100">
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
              Why Choose Sailors Bail Bonds?
            </h2>
            
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">2-Min Response</h3>
                <p className="text-sm text-gray-600">Guaranteed agent contact within 2 minutes</p>
              </div>

              <div className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Licensed Agents</h3>
                <p className="text-sm text-gray-600">15+ years experience serving Sacramento</p>
              </div>

              <div className="text-center">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">24/7 Available</h3>
                <p className="text-sm text-gray-600">Never closed - always here when you need us</p>
              </div>

              <div className="text-center">
                <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-yellow-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Kind Service</h3>
                <p className="text-sm text-gray-600">Compassionate, understanding approach</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default ContactUs;